import { useEffect, useMemo, useState } from 'react';
import { chunkOfId } from '@versesverse/core';
import type { VerseChunkArtifact } from '@versesverse/schema/artifacts';
import { buildPassages } from './passages';
import { makeTextOf } from './load';
import type { Passage } from './passages';
import type { VerseSource, VerseTables } from './useNodeVerses';

/*
 * TWO RUNS OF VERSES, LOADED TO READ SIDE BY SIDE.
 *
 * A shared story (the same episode in the Bible and the Quran) and a retelling (a chapter told twice
 * inside one scripture — Chronicles over Samuel) are the same shape: two lists of verse ids to show
 * beside each other. This fetches the ranges that hold both — once, together — and shapes each side
 * with the same builder, so a verse reads exactly as it does anywhere else: original, named English,
 * clickable names. What the two sides MEAN is the caller's to label; this only turns their ids into
 * text.
 */
export interface SideRef {
  left: { ids: number[] };
  right: { ids: number[] };
}

export interface SideState {
  status: 'idle' | 'loading' | 'ready';
  left: Passage[];
  right: Passage[];
  incomplete: boolean;
}

export function useSidePassages(source: VerseSource, tables: VerseTables, sides: SideRef | null): SideState {
  const [loaded, setLoaded] = useState<{ key: string; chunks: Map<number, VerseChunkArtifact>; incomplete: boolean } | null>(null);

  /* One string identity for both sides, so the effect re-runs only on a genuinely different pairing. */
  const key = sides ? `${sides.left.ids.join(',')}|${sides.right.ids.join(',')}` : '';

  useEffect(() => {
    if (!sides) { setLoaded(null); return; }
    let live = true;
    setLoaded(null); // clear the previous pairing so the view shows loading, not stale text
    void (async () => {
      const ids = [...sides.left.ids, ...sides.right.ids];
      const need = [...new Set(ids.map(id => chunkOfId(tables.chunkStarts, id)))];
      const chunks = new Map<number, VerseChunkArtifact>();
      let incomplete = false;
      await Promise.all(need.map(async chunk => {
        try { chunks.set(chunk, await source.verseChunk(chunk)); } catch { incomplete = true; }
      }));
      if (live) setLoaded({ key, chunks, incomplete });
    })();
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps -- source/tables stable; ids captured by key

  return useMemo<SideState>(() => {
    if (!sides) return { status: 'idle', left: [], right: [], incomplete: false };
    if (!loaded || loaded.key !== key) return { status: 'loading', left: [], right: [], incomplete: false };
    const textOf = makeTextOf(loaded.chunks, tables.chunkStarts);
    return {
      status: 'ready',
      left: buildPassages(sides.left.ids, textOf, tables),
      right: buildPassages(sides.right.ids, textOf, tables),
      incomplete: loaded.incomplete,
    };
  }, [key, loaded, sides, tables]);
}
